/**
 * How each injection verdict reads, in one place.
 *
 * Shared because the same vocabulary now appears on the agent card, on the
 * component inventory and in the teaser, and three copies of a verdict label
 * is how one of them ends up saying "secure".
 *
 * Only `reachable` is coloured as a finding. `not_assessable` is grey with a
 * question mark — the house convention for "we could not judge this", used by
 * Compliance Posture's `not_checked` and by `ComponentsView`'s `unknown` — and
 * deliberately NOT amber, because amber reads as a lesser finding and this is
 * not a finding at all.
 *
 * `controlled` is `info` rather than `success`: a control was found on every
 * hop, which is worth seeing and is not an all-clear. Green here would teach a
 * reader that the absence of a finding is a pass, which is the one reading the
 * backend's verdict vocabulary was built to prevent.
 */
import { InjectionVerdict } from '../services/api'

export const VERDICT: Record<
  InjectionVerdict,
  { label: string; color: 'error' | 'info' | 'default'; meaning: string }
> = {
  reachable: {
    label: 'reachable',
    color: 'error',
    meaning:
      'Point your next injection test here. Content nobody in your '
      + 'repositories wrote reaches an agent that changes something, and '
      + 'nothing on the way validates it.',
  },
  not_assessable: {
    label: 'not assessed',
    color: 'default',
    meaning:
      'No conclusion: not a finding, and not a pass either. Something on '
      + 'the route could not be judged: an unfamiliar source, a tool whose '
      + 'capability we could not place, or a step we could not read. Worth '
      + 'a look by hand if the agent at the end of it matters.',
  },
  controlled: {
    label: 'controlled',
    color: 'info',
    meaning:
      'Lower priority than a reachable route. The route exists and something '
      + 'validates on it — which is not a guarantee that the control is '
      + 'sufficient, only that one is there.',
  },
  not_reachable_in_scanned_code: {
    label: 'not reachable in scanned code',
    color: 'default',
    meaning:
      'Nothing to test here, on the evidence we have. A route could still be '
      + 'created at run time — by dynamic dispatch, a tool named in an '
      + 'environment variable, or configuration injected at deploy — and '
      + 'none of those are visible to a static read.',
  },
}

/** What a route IS, for a reader meeting the word for the first time.
 *
 *  The column was headed "Route verdict" with no explanation of either word,
 *  and the chips' own tooltips described the verdict without ever saying what
 *  it was a verdict ABOUT. A reader could learn that something was "reachable"
 *  without learning what had reached what.
 */
export const ROUTE_MEANING =
  'A route is a path by which content nobody in your repositories wrote — a '
  + 'document, an indexed page, another system\'s output — can reach an agent '
  + 'that changes something. The verdict says whether that path is live, and '
  + 'whether anything validates the content on the way.'

/** The server's own display order — `reachable`, then `not_assessable`, then
 *  `controlled`, then the negative verdict. `not_assessable` ranks above
 *  `controlled` because one is a finding and the other is a gap in what we
 *  could see; they are not the same priority. */
export const VERDICT_ORDER: InjectionVerdict[] = [
  'reachable', 'not_assessable', 'controlled', 'not_reachable_in_scanned_code',
]

/** The worst verdict present, by that order. Used where a card has room for
 *  one chip and not four. */
export const worstVerdict = (
  verdicts: InjectionVerdict[],
): InjectionVerdict | null => {
  for (const v of VERDICT_ORDER) if (verdicts.includes(v)) return v
  return null
}
